// src/components/SolicitudConstruccionModal.js
import React, {
    useCallback,
    useEffect,
    useMemo,
    useState,
} from 'react';
import PropTypes from 'prop-types';
import {
    FaArrowLeft,
    FaArrowRight,
    FaCheckCircle,
    FaCloudUploadAlt,
    FaTimes,
} from 'react-icons/fa';
import {
    Timestamp,
    collection,
    doc,
    getDoc,
    getDocs,
    limit,
    onSnapshot,
    query,
    serverTimestamp,
    setDoc,
    where,
} from 'firebase/firestore';
import { getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import { db, storage } from '../firebase';
import { registerMovement } from '../utils/registerMovement';
import './SolicitudMudanzaModal.css';

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const ACCEPTED_TYPES = ['application/pdf', 'image/png', 'image/jpeg'];

const STEP_TITLES = [
    'Información del Propietario',
    'Detalles de la Obra',
    'Documentos requeridos',
    'Confirmar solicitud',
];

const TIPO_CONSTRUCCION_OPTIONS = [
    'Obra nueva',
    'Ampliación',
    'Remodelación mayor',
    'Mantenimiento estructural',
];

const pickFirstString = (values = []) => {
    for (const raw of values) {
        if (raw == null) {
            continue;
        }
        if (typeof raw === 'string') {
            const trimmed = raw.trim();
            if (trimmed.length > 0) {
                return trimmed;
            }
        } else if (typeof raw === 'number') {
            const converted = String(raw);
            if (converted.trim().length > 0) {
                return converted.trim();
            }
        }
    }
    return '';
};

const buildCasaCandidates = (userData) => {
    if (!userData || typeof userData !== 'object') {
        return [];
    }
    const rawCandidates = [
        userData.numeroCasa,
        userData.numeroLote,
        userData.numero,
        userData.lote,
        userData.houseLabel,
        userData.houseNumber,
        userData.loteNumero,
        userData.loteId,
        userData.casaNumero,
        userData.casaId,
        userData.identificadorCasa,
        userData.houseId,
        userData.casaCodigo,
        userData?.casa?.numero,
        userData?.casaAsignada?.numero,
        userData?.direccion?.numero,
    ];
    const seen = new Set();
    const result = [];
    rawCandidates.forEach((raw) => {
        if (raw == null) {
            return;
        }
        const candidate = typeof raw === 'number' ? String(raw) : String(raw || '').trim();
        if (candidate && !seen.has(candidate)) {
            seen.add(candidate);
            result.push(candidate);
        }
        if (candidate) {
            const compact = candidate.replace(/\s+/g, '');
            if (compact && !seen.has(compact)) {
                seen.add(compact);
                result.push(compact);
            }
            const match = candidate.match(/(\d{1,5}[\w-]*)/);
            if (match && match[1]) {
                const extracted = match[1];
                if (!seen.has(extracted)) {
                    seen.add(extracted);
                    result.push(extracted);
                }
            }
        }
    });
    return result;
};

const buildInitialForm = (userData) => ({
    nombreCompleto: pickFirstString([
        userData?.nombreCompleto,
        `${userData?.nombre || ''} ${userData?.apellidos || ''}`.trim(),
        userData?.displayName,
    ]),
    lote: pickFirstString([
        userData?.numeroCasa,
        userData?.numeroLote,
        userData?.lote,
        userData?.houseLabel,
    ]),
    telefonoPrincipal: pickFirstString([
        userData?.telefono,
        userData?.telefonoPrincipal,
        userData?.phoneNumber,
    ]),
    email: pickFirstString([
        userData?.correo,
        userData?.email,
        userData?.emailPrincipal,
    ]).toLowerCase(),
    empresaConstructora: '',
    encargadoObra: '',
    telefonoEncargado: '',
    rfcConstructora: '',
    tipoConstruccion: '',
    descripcionConstruccion: '',
    metrosCuadrados: '',
    niveles: '',
    fechaInicioObra: '',
    fechaTerminoObra: '',
    horarioLlegada: '',
    aceptaReglamento: false,
});

const defaultFiles = {
    licencia: null,
    planos: null,
    identificacion: null,
    predial: null,
    cronograma: null,
};

const uploadSolicitudFile = async (fraccionamientoId, userId, solicitudId, slotKey, file) => {
    if (!file) {
        return null;
    }
    const storageRef = ref(storage, `fraccionamientos/${fraccionamientoId}/solicitudes/${solicitudId}/${slotKey}-${file.name}`);
    const snapshot = await uploadBytes(storageRef, file);
    const url = await getDownloadURL(snapshot.ref);
    return {
        url,
        name: file.name,
        size: file.size,
        type: file.type,
    };
};

const formatDateForSummary = (raw) => {
    if (!raw) {
        return '-';
    }
    try {
        const date = new Date(raw);
        if (Number.isNaN(date.getTime())) {
            return '-';
        }
        return date.toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' });
    } catch (error) {
        return '-';
    }
};

const TIME_PATTERN = /^([01]\d|2[0-3]):([0-5]\d)$/;

const DEFAULT_SCHEDULE = {
    diasPermitidos: ['mon', 'tue', 'wed', 'thu', 'fri'],
    horaInicio: '08:00',
    horaFin: '18:00',
};

const DAY_KEY_BY_INDEX = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
const DAY_ORDER = DAY_KEY_BY_INDEX.reduce((acc, key, index) => {
    acc[key] = index;
    return acc;
}, {});

const DAY_LABEL = {
    mon: 'Lunes',
    tue: 'Martes',
    wed: 'Miércoles',
    thu: 'Jueves',
    fri: 'Viernes',
    sat: 'Sábado',
    sun: 'Domingo',
};

const sanitizeScheduleConfig = (rawConfig) => {
    const source = rawConfig && typeof rawConfig === 'object' ? rawConfig : {};

    const rawDays = Array.isArray(source.diasPermitidos) ? source.diasPermitidos : [];
    const diasSanitized = rawDays
        .map((day) => (typeof day === 'string' ? day.toLowerCase().trim() : ''))
        .filter((day) => DAY_LABEL[day])
        .sort((a, b) => (DAY_ORDER[a] || 0) - (DAY_ORDER[b] || 0));

    const diasPermitidos = diasSanitized.length
        ? diasSanitized
        : (Array.isArray(source.diasPermitidos) ? [] : [...DEFAULT_SCHEDULE.diasPermitidos]);

    const horaInicio = typeof source.horaInicio === 'string' && TIME_PATTERN.test(source.horaInicio)
        ? source.horaInicio
        : DEFAULT_SCHEDULE.horaInicio;
    const horaFin = typeof source.horaFin === 'string' && TIME_PATTERN.test(source.horaFin)
        ? source.horaFin
        : DEFAULT_SCHEDULE.horaFin;

    return {
        diasPermitidos: diasPermitidos.length ? diasPermitidos : [...DEFAULT_SCHEDULE.diasPermitidos],
        horaInicio,
        horaFin,
    };
};

const formatTimeLabel = (timeValue) => {
    if (typeof timeValue !== 'string' || !TIME_PATTERN.test(timeValue)) {
        return '';
    }
    const [hoursRaw, minutes] = timeValue.split(':');
    const hours = Number(hoursRaw);
    if (Number.isNaN(hours)) {
        return '';
    }
    const suffix = hours >= 12 ? 'p.m.' : 'a.m.';
    const normalizedHour = ((hours + 11) % 12) + 1;
    const paddedHour = `${normalizedHour}`.padStart(2, '0');
    return `${paddedHour}:${minutes} ${suffix}`;
};

const generateTimeSlots = (start, end, intervalMinutes = 60) => {
    const slots = [];
    if (!start || !end) {
        return slots;
    }
    const interval = Number(intervalMinutes) > 0 ? Number(intervalMinutes) : 60;
    const [startHour, startMinute] = start.split(':').map(Number);
    const [endHour, endMinute] = end.split(':').map(Number);
    if ([startHour, startMinute, endHour, endMinute].some(Number.isNaN)) {
        return slots;
    }
    const base = new Date();
    base.setSeconds(0, 0);
    base.setMilliseconds(0);
    const current = new Date(base);
    current.setHours(startHour, startMinute, 0, 0);
    const limit = new Date(base);
    limit.setHours(endHour, endMinute, 0, 0);
    let guard = 0;
    while (current <= limit && guard < 2880) {
        const hours = `${current.getHours()}`.padStart(2, '0');
        const minutes = `${current.getMinutes()}`.padStart(2, '0');
        slots.push(`${hours}:${minutes}`);
        current.setMinutes(current.getMinutes() + interval);
        guard += 1;
    }
    return slots;
};

const SolicitudConstruccionModal = ({
    fraccionamientoId,
    currentUser,
    userData = null,
    onClose,
    onSuccess = null,
}) => {
    const [step, setStep] = useState(0);
    const [form, setForm] = useState(() => buildInitialForm(userData));
    const [files, setFiles] = useState(defaultFiles);
    const [error, setError] = useState('');
    const [saving, setSaving] = useState(false);
    const [residentCasaData, setResidentCasaData] = useState(null);
    const [loadingCasaData, setLoadingCasaData] = useState(false);

    const scheduleDefaults = useMemo(() => sanitizeScheduleConfig(DEFAULT_SCHEDULE), []);
    const [scheduleConfig, setScheduleConfig] = useState(scheduleDefaults);
    const [allowedDays, setAllowedDays] = useState(scheduleDefaults.diasPermitidos);

    const casaCandidates = useMemo(() => buildCasaCandidates(userData), [userData]);
    const uidCandidates = useMemo(() => [
        currentUser?.uid,
        userData?.uid,
        userData?.userId,
        userData?.usuarioId,
    ].filter((value) => typeof value === 'string' && value.trim().length > 0), [currentUser?.uid, userData?.uid, userData?.userId, userData?.usuarioId]);

    useEffect(() => {
        const shouldSkipCasaLookup = !fraccionamientoId
            || (((!Array.isArray(casaCandidates) || casaCandidates.length === 0)
                && uidCandidates.length === 0));

        if (shouldSkipCasaLookup) {
            setResidentCasaData(null);
            setLoadingCasaData(false);
            return undefined;
        }

        let cancelled = false;
        const fetchCasaData = async () => {
            setLoadingCasaData(true);
            try {
                const casasRef = collection(db, 'fraccionamientos', fraccionamientoId, 'casas');
                const tryAssignCasaFromDocRef = async (docRef) => {
                    if (cancelled) {
                        return false;
                    }
                    const snap = await getDoc(docRef);
                    if (snap?.exists()) {
                        setResidentCasaData({ id: snap.id, ...snap.data() });
                        return true;
                    }
                    return false;
                };

                for (const candidate of casaCandidates) {
                    const normalized = typeof candidate === 'string' ? candidate.trim() : String(candidate || '').trim();
                    if (!normalized) {
                        continue;
                    }
                    try {
                        const directDocRef = doc(casasRef, normalized);
                        const directMatch = await tryAssignCasaFromDocRef(directDocRef);
                        if (directMatch) {
                            return;
                        }

                        const snapshot = await getDocs(query(casasRef, where('numero', '==', normalized), limit(1)));
                        if (!snapshot.empty) {
                            if (!cancelled) {
                                const docSnap = snapshot.docs[0];
                                setResidentCasaData({ id: docSnap.id, ...docSnap.data() });
                            }
                            return;
                        }
                    } catch (innerError) {
                        console.error('SolicitudConstruccionModal: error buscando casa por número', normalized, innerError);
                    }
                }

                const uidFields = ['uid', 'userId', 'usuarioId', 'residenteUid', 'residenteId', 'propietarioUid', 'propietarioId'];
                for (const field of uidFields) {
                    for (const uid of uidCandidates) {
                        const normalizedUid = uid.trim();
                        try {
                            const snapshot = await getDocs(query(casasRef, where(field, '==', normalizedUid), limit(1)));
                            if (!snapshot.empty) {
                                if (!cancelled) {
                                    const docSnap = snapshot.docs[0];
                                    setResidentCasaData({ id: docSnap.id, ...docSnap.data() });
                                }
                                return;
                            }
                        } catch (innerError) {
                            console.error(`SolicitudConstruccionModal: error buscando casa por ${field}`, normalizedUid, innerError);
                        }
                    }
                }

                if (!cancelled) {
                    setResidentCasaData(null);
                }
            } catch (fetchError) {
                console.error('SolicitudConstruccionModal: no se pudo obtener información de la casa', fetchError);
                if (!cancelled) {
                    setResidentCasaData(null);
                }
            } finally {
                if (!cancelled) {
                    setLoadingCasaData(false);
                }
            }
        };

        fetchCasaData();

        return () => {
            cancelled = true;
        };
    }, [casaCandidates, fraccionamientoId, uidCandidates]);

    const defaultNombre = useMemo(() => pickFirstString([
        residentCasaData?.nombre_residente,
        residentCasaData?.nombre,
        residentCasaData?.titular,
        userData?.nombreCompleto,
        `${userData?.nombre || ''} ${userData?.apellidos || ''}`.trim(),
        userData?.displayName,
    ]), [residentCasaData, userData]);

    const defaultTelefono = useMemo(() => pickFirstString([
        residentCasaData?.telefono,
        residentCasaData?.telefono_principal,
        userData?.telefono,
        userData?.telefonoPrincipal,
        userData?.phoneNumber,
    ]), [residentCasaData, userData]);

    const defaultEmail = useMemo(() => pickFirstString([
        residentCasaData?.email,
        userData?.correo,
        userData?.email,
        userData?.emailPrincipal,
    ]), [residentCasaData, userData]);

    const defaultLote = useMemo(() => pickFirstString([
        residentCasaData?.numero,
        residentCasaData?.numeroCasa,
        residentCasaData?.lote,
        userData?.numeroCasa,
        userData?.numeroLote,
        userData?.lote,
        userData?.houseLabel,
    ]), [residentCasaData, userData]);

    useEffect(() => {
        console.log('SolicitudConstruccionModal - residentCasaData:', residentCasaData);
        console.log('SolicitudConstruccionModal - valores calculados:', {
            defaultNombre,
            defaultTelefono,
            defaultEmail,
            defaultLote,
        });
        
        setForm((prev) => ({
            ...prev,
            nombreCompleto: defaultNombre || prev.nombreCompleto,
            telefonoPrincipal: defaultTelefono || prev.telefonoPrincipal,
            email: (defaultEmail || prev.email).toLowerCase(),
            lote: defaultLote || prev.lote,
        }));
    }, [residentCasaData, defaultNombre, defaultTelefono, defaultEmail, defaultLote]);

    useEffect(() => {
        if (!fraccionamientoId) {
            setScheduleConfig(scheduleDefaults);
            setAllowedDays(scheduleDefaults.diasPermitidos);
            return () => {};
        }

        const configRef = doc(db, 'fraccionamientos', fraccionamientoId, 'configuraciones', 'permisosEstructurales');
        const unsubscribe = onSnapshot(
            configRef,
            (snapshot) => {
                if (!snapshot.exists()) {
                    setScheduleConfig(scheduleDefaults);
                    setAllowedDays(scheduleDefaults.diasPermitidos);
                    return;
                }

                const data = snapshot.data() || {};
                const categorias = (typeof data.categorias === 'object' && data.categorias !== null)
                    ? data.categorias
                    : data;
                const config = sanitizeScheduleConfig(categorias?.construccion || {});
                setScheduleConfig(config);
                setAllowedDays(config.diasPermitidos);
            },
            (listenerError) => {
                console.error('SolicitudConstruccionModal: no se pudo obtener configuración de permisos estructurales', listenerError);
                setScheduleConfig(scheduleDefaults);
                setAllowedDays(scheduleDefaults.diasPermitidos);
            },
        );

        return unsubscribe;
    }, [fraccionamientoId, scheduleDefaults]);

    const isDayAllowed = useCallback((dateString) => {
        if (!dateString) {
            return true;
        }
        const date = new Date(`${dateString}T00:00:00`);
        if (Number.isNaN(date.getTime())) {
            return false;
        }
        const key = DAY_KEY_BY_INDEX[date.getDay()];
        if (!key) {
            return false;
        }
        return allowedDays.includes(key);
    }, [allowedDays]);

    const horarioSlots = useMemo(() => {
        const interval = scheduleConfig.intervaloMinutos || 60;
        return generateTimeSlots(scheduleConfig.horaInicio, scheduleConfig.horaFin, interval);
    }, [scheduleConfig.horaInicio, scheduleConfig.horaFin, scheduleConfig.intervaloMinutos]);

    useEffect(() => {
        let removed = false;
        setForm((prev) => {
            let next = prev;
            if (prev.fechaInicioObra && !isDayAllowed(prev.fechaInicioObra)) {
                next = next === prev ? { ...next, fechaInicioObra: '' } : { ...next, fechaInicioObra: '' };
                removed = true;
            }
            if (prev.fechaTerminoObra && !isDayAllowed(prev.fechaTerminoObra)) {
                next = next === prev ? { ...next, fechaTerminoObra: '' } : { ...next, fechaTerminoObra: '' };
                removed = true;
            }
            return next;
        });
        if (removed) {
            setError('Las fechas seleccionadas ya no están disponibles para permisos de construcción según la configuración de administración.');
        }
    }, [isDayAllowed]);

    const allowedDaySummary = useMemo(() => {
        if (!allowedDays.length) {
            return '';
        }
        return allowedDays.map((day) => DAY_LABEL[day] || day).join(', ');
    }, [allowedDays]);

    const timeRangeLabel = useMemo(() => {
        const start = formatTimeLabel(scheduleConfig.horaInicio);
        const end = formatTimeLabel(scheduleConfig.horaFin);
        if (start && end) {
            return `${start} - ${end}`;
        }
        if (start) {
            return `Desde ${start}`;
        }
        if (end) {
            return `Hasta ${end}`;
        }
        return '';
    }, [scheduleConfig.horaFin, scheduleConfig.horaInicio]);

    const scheduleSummaryText = useMemo(() => {
        const days = allowedDaySummary || 'Sin días disponibles';
        return timeRangeLabel ? `${days} · ${timeRangeLabel}` : days;
    }, [allowedDaySummary, timeRangeLabel]);

    const uploaderName = useMemo(() => pickFirstString([
        userData?.nombreCompleto,
        userData?.nombre,
        userData?.displayName,
        form.nombreCompleto,
    ]), [form.nombreCompleto, userData?.displayName, userData?.nombre, userData?.nombreCompleto]);

    const uploaderRole = useMemo(() => {
        if (typeof userData?.rol === 'string') {
            const normalized = userData.rol.trim().toLowerCase();
            if (normalized) {
                return normalized;
            }
        }
        return 'residente';
    }, [userData?.rol]);

    const documentCount = useMemo(() => 4 + (files.cronograma ? 1 : 0), [files.cronograma]);

    const handleInputChange = useCallback((event) => {
        const { name, value, type, checked } = event.target;

        if ((name === 'fechaInicioObra' || name === 'fechaTerminoObra') && value && !isDayAllowed(value)) {
            setError('La fecha seleccionada no está disponible para permisos de construcción según la configuración de administración.');
            return;
        }

        setForm((prev) => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value,
        }));

        if (name === 'fechaInicioObra' || name === 'fechaTerminoObra') {
            setError('');
        }
    }, [isDayAllowed]);

    const handleFileSelect = useCallback((event, key) => {
        const file = event.target.files && event.target.files[0];
        if (!file) {
            return;
        }
        if (!ACCEPTED_TYPES.includes(file.type)) {
            setError('Solo se permiten archivos PDF, JPG o PNG.');
            return;
        }
        if (file.size > MAX_FILE_SIZE) {
            setError('Cada archivo debe pesar menos de 10 MB.');
            return;
        }
        setError('');
        setFiles((prev) => ({ ...prev, [key]: file }));
    }, []);

    const handleRemoveFile = useCallback((key) => {
        setFiles((prev) => ({ ...prev, [key]: null }));
    }, []);

    const validateStep = useCallback((currentStep) => {
        switch (currentStep) {
            case 0:
                if (!form.nombreCompleto.trim()) {
                    setError('Ingresa el nombre completo del propietario.');
                    return false;
                }
                if (!form.lote.trim()) {
                    setError('Ingresa el número de lote.');
                    return false;
                }
                if (!form.telefonoPrincipal.trim()) {
                    setError('Captura un teléfono de contacto.');
                    return false;
                }
                if (!form.email.trim()) {
                    setError('Ingresa un correo electrónico.');
                    return false;
                }
                if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
                    setError('El correo electrónico no tiene un formato válido.');
                    return false;
                }
                if (!form.empresaConstructora.trim()) {
                    setError('Ingresa el nombre de la empresa constructora.');
                    return false;
                }
                if (!form.encargadoObra.trim()) {
                    setError('Captura el nombre del encargado de obra.');
                    return false;
                }
                if (!form.telefonoEncargado.trim()) {
                    setError('Ingresa el teléfono del encargado.');
                    return false;
                }
                setError('');
                return true;
            case 1:
                if (!form.tipoConstruccion.trim()) {
                    setError('Selecciona el tipo de construcción.');
                    return false;
                }
                if (!form.descripcionConstruccion.trim()) {
                    setError('Describe brevemente la obra.');
                    return false;
                }
                if (!form.metrosCuadrados || Number(form.metrosCuadrados) <= 0) {
                    setError('Indica los metros cuadrados de la obra.');
                    return false;
                }
                if (!form.niveles || Number(form.niveles) <= 0) {
                    setError('Indica el número de niveles.');
                    return false;
                }
                if (!form.fechaInicioObra) {
                    setError('Selecciona la fecha de inicio de la obra.');
                    return false;
                }
                if (!form.fechaTerminoObra) {
                    setError('Selecciona la fecha estimada de término.');
                    return false;
                }
                if (!form.horarioLlegada) {
                    setError('Selecciona el horario de llegada del contratista.');
                    return false;
                }
                if (!isDayAllowed(form.fechaInicioObra) || !isDayAllowed(form.fechaTerminoObra)) {
                    setError('Las fechas seleccionadas no están disponibles para permisos de construcción según la configuración de administración.');
                    return false;
                }
                if (form.fechaInicioObra && form.fechaTerminoObra && form.fechaInicioObra > form.fechaTerminoObra) {
                    setError('La fecha de término debe ser posterior o igual a la de inicio.');
                    return false;
                }
                setError('');
                return true;
            case 2:
                if (!files.licencia) {
                    setError('Adjunta la licencia municipal de construcción.');
                    return false;
                }
                if (!files.planos) {
                    setError('Adjunta los planos arquitectónicos.');
                    return false;
                }
                if (!files.identificacion) {
                    setError('Adjunta la identificación del propietario.');
                    return false;
                }
                if (!files.predial) {
                    setError('Adjunta el recibo de predial.');
                    return false;
                }
                setError('');
                return true;
            case 3:
                if (!form.aceptaReglamento) {
                    setError('Debes aceptar el reglamento de construcción.');
                    return false;
                }
                setError('');
                return true;
            default:
                return true;
        }
    }, [files.identificacion, files.licencia, files.planos, files.predial, form, isDayAllowed]);

    const handleNext = useCallback(() => {
        if (validateStep(step)) {
            setStep((prev) => Math.min(prev + 1, STEP_TITLES.length - 1));
        }
    }, [step, validateStep]);

    const handlePrev = useCallback(() => {
        setError('');
        setStep((prev) => Math.max(prev - 1, 0));
    }, []);

    const handleSubmit = useCallback(async () => {
        if (!validateStep(3)) {
            return;
        }
        if (!fraccionamientoId || !currentUser?.uid) {
            setError('No pudimos validar tu sesión. Intenta nuevamente.');
            return;
        }
        try {
            setSaving(true);
            setError('');
            const documentosRef = collection(db, 'fraccionamientos', fraccionamientoId, 'documentos');
            const solicitudRef = doc(documentosRef);

            const [licenciaInfo, planosInfo, identificacionInfo, predialInfo, cronogramaInfo] = await Promise.all([
                uploadSolicitudFile(fraccionamientoId, currentUser.uid, solicitudRef.id, 'licencia', files.licencia),
                uploadSolicitudFile(fraccionamientoId, currentUser.uid, solicitudRef.id, 'planos', files.planos),
                uploadSolicitudFile(fraccionamientoId, currentUser.uid, solicitudRef.id, 'identificacion', files.identificacion),
                uploadSolicitudFile(fraccionamientoId, currentUser.uid, solicitudRef.id, 'predial', files.predial),
                files.cronograma
                    ? uploadSolicitudFile(fraccionamientoId, currentUser.uid, solicitudRef.id, 'cronograma', files.cronograma)
                    : Promise.resolve(null),
            ]);

            const inicioDate = new Date(`${form.fechaInicioObra}T12:00:00`);
            const terminoDate = new Date(`${form.fechaTerminoObra}T12:00:00`);
            if (Number.isNaN(inicioDate.getTime()) || Number.isNaN(terminoDate.getTime())) {
                throw new Error('Fechas de obra inválidas');
            }

            const payload = {
                segmento: 'solicitudes',
                titulo: 'Permiso de Construcción',
                descripcion: `Permiso de construcción para ${form.lote || 'lote sin especificar'}`,
                tipoSolicitud: 'construccion',
                fraccionamientoId,
                createdAt: serverTimestamp(),
                uploadedBy: currentUser.uid,
                uploaderNombre: uploaderName || form.nombreCompleto.trim(),
                uploaderRol: uploaderRole,
                estado: 'pendiente',
                nombreCompleto: form.nombreCompleto.trim(),
                lote: form.lote.trim(),
                telefonoPrincipal: form.telefonoPrincipal.trim(),
                email: form.email.trim().toLowerCase(),
                empresaConstructora: form.empresaConstructora.trim(),
                encargadoObra: form.encargadoObra.trim(),
                telefonoEncargado: form.telefonoEncargado.trim(),
                rfcConstructora: form.rfcConstructora.trim() || null,
                tipoConstruccion: form.tipoConstruccion.trim(),
                descripcionConstruccion: form.descripcionConstruccion.trim(),
                metrosCuadrados: Number(form.metrosCuadrados),
                niveles: Number(form.niveles),
                fechaInicioObra: Timestamp.fromDate(inicioDate),
                fechaTerminoObra: Timestamp.fromDate(terminoDate),
                horarioLlegada: form.horarioLlegada.trim(),
                documentosAdjuntos: documentCount,
                docLicenciaUrl: licenciaInfo.url,
                docLicenciaName: licenciaInfo.name,
                docLicenciaSize: licenciaInfo.size,
                docLicenciaType: licenciaInfo.type,
                docPlanosUrl: planosInfo.url,
                docPlanosName: planosInfo.name,
                docPlanosSize: planosInfo.size,
                docPlanosType: planosInfo.type,
                docIdentificacionUrl: identificacionInfo.url,
                docIdentificacionName: identificacionInfo.name,
                docIdentificacionSize: identificacionInfo.size,
                docIdentificacionType: identificacionInfo.type,
                docPredialUrl: predialInfo.url,
                docPredialName: predialInfo.name,
                docPredialSize: predialInfo.size,
                docPredialType: predialInfo.type,
            };

            if (cronogramaInfo) {
                payload.docCronogramaUrl = cronogramaInfo.url;
                payload.docCronogramaName = cronogramaInfo.name;
                payload.docCronogramaSize = cronogramaInfo.size;
                payload.docCronogramaType = cronogramaInfo.type;
            }

            await setDoc(solicitudRef, payload);

            try {
                const inicioLabel = inicioDate.toLocaleDateString('es-MX');
                const finLabel = terminoDate.toLocaleDateString('es-MX');
                await registerMovement({
                    fraccionamientoId,
                    titulo: `Solicitud de construcción registrada - ${payload.uploaderNombre || 'Residente'}`,
                    descripcion: `${form.tipoConstruccion || 'Construcción'} · ${inicioLabel} - ${finLabel}`,
                    categoria: 'Solicitudes y Permisos',
                    tipoMovimiento: 'solicitud_creada',
                    origen: 'solicitudes',
                    metadata: {
                        solicitudId: solicitudRef.id,
                        tipoSolicitud: 'construccion',
                        estado: 'pendiente',
                        fechaInicioIso: inicioDate.toISOString(),
                        fechaTerminoIso: terminoDate.toISOString(),
                        lote: form.lote.trim() || null,
                        empresaConstructora: form.empresaConstructora.trim() || null,
                    },
                });
            } catch (logError) {
                console.error('SolicitudConstruccionModal: no se pudo registrar el movimiento de creación', logError);
            }

            if (onSuccess) {
                onSuccess('Solicitud de construcción enviada. Te avisaremos cuando la administración la revise.');
            }
        } catch (err) {
            console.error('SolicitudConstruccionModal: error al registrar la solicitud', err);
            setError('No pudimos registrar la solicitud. Intenta nuevamente.');
            setSaving(false);
            return;
        }
        setSaving(false);
    }, [currentUser, documentCount, files.cronograma, files.identificacion, files.licencia, files.planos, files.predial, form, fraccionamientoId, onSuccess, uploaderName, uploaderRole, validateStep]);

    const handleOverlayClick = (event) => {
        if (event.target === event.currentTarget && !saving) {
            onClose();
        }
    };

    return (
        <div className="mudanza-modal-overlay" role="presentation" onClick={handleOverlayClick}>
            <div className="mudanza-modal" role="dialog" aria-modal="true" aria-labelledby="construccionModalTitle">
                <header className="mudanza-modal-header">
                    <div className="mudanza-header-meta">
                        <span className="mudanza-template-badge">Permiso de Construcción</span>
                        <h2 id="construccionModalTitle">Nueva solicitud</h2>
                    </div>
                    <button type="button" className="mudanza-close" onClick={onClose} aria-label="Cerrar" disabled={saving}>
                        <FaTimes />
                    </button>
                </header>

                <nav className="mudanza-steps" aria-label="Progreso de la solicitud">
                    {STEP_TITLES.map((title, index) => {
                        const status = index === step ? 'active' : (index < step ? 'complete' : 'pending');
                        return (
                            <div key={title} className={`mudanza-step ${status}`}>
                                <span className="mudanza-step-index">{index + 1}</span>
                                <span className="mudanza-step-title">{title}</span>
                            </div>
                        );
                    })}
                </nav>

                {error && (
                    <div className="mudanza-error" role="alert">{error}</div>
                )}

                <div className="mudanza-body">
                    {step === 0 && (
                        <div className="mudanza-section">
                            <h3>Información del Propietario</h3>
                            <div className="mudanza-grid">
                                <label className="mudanza-field">
                                    <span>Nombre completo *</span>
                                    <input
                                        type="text"
                                        name="nombreCompleto"
                                        value={form.nombreCompleto}
                                        onChange={handleInputChange}
                                        placeholder="Nombre del propietario"
                                        required
                                    />
                                </label>
                                <label className="mudanza-field">
                                    <span>Número de lote *</span>
                                    <input
                                        type="text"
                                        name="lote"
                                        value={form.lote}
                                        onChange={handleInputChange}
                                        placeholder="Ej. B-22"
                                        required
                                    />
                                </label>
                                <label className="mudanza-field">
                                    <span>Teléfono *</span>
                                    <input
                                        type="tel"
                                        name="telefonoPrincipal"
                                        value={form.telefonoPrincipal}
                                        onChange={handleInputChange}
                                        placeholder="442 123 4567"
                                        required
                                    />
                                </label>
                                <label className="mudanza-field">
                                    <span>Email *</span>
                                    <input
                                        type="email"
                                        name="email"
                                        value={form.email}
                                        onChange={handleInputChange}
                                        placeholder="correo@ejemplo.com"
                                        required
                                    />
                                </label>
                            </div>

                            <h3>Datos del Constructor</h3>
                            <div className="mudanza-grid">
                                <label className="mudanza-field">
                                    <span>Empresa constructora *</span>
                                    <input
                                        type="text"
                                        name="empresaConstructora"
                                        value={form.empresaConstructora}
                                        onChange={handleInputChange}
                                        placeholder="Razón social"
                                        required
                                    />
                                </label>
                                <label className="mudanza-field">
                                    <span>Encargado de obra *</span>
                                    <input
                                        type="text"
                                        name="encargadoObra"
                                        value={form.encargadoObra}
                                        onChange={handleInputChange}
                                        placeholder="Nombre"
                                        required
                                    />
                                </label>
                                <label className="mudanza-field">
                                    <span>Teléfono encargado *</span>
                                    <input
                                        type="tel"
                                        name="telefonoEncargado"
                                        value={form.telefonoEncargado}
                                        onChange={handleInputChange}
                                        placeholder="Teléfono"
                                        required
                                    />
                                </label>
                                <label className="mudanza-field">
                                    <span>RFC</span>
                                    <input
                                        type="text"
                                        name="rfcConstructora"
                                        value={form.rfcConstructora}
                                        onChange={handleInputChange}
                                        placeholder="RFC"
                                    />
                                </label>
                            </div>
                        </div>
                    )}

                    {step === 1 && (
                        <div className="mudanza-section">
                            <h3>Detalles de la Obra</h3>
                            <div className="mudanza-grid">
                                <label className="mudanza-field">
                                    <span>Tipo de construcción *</span>
                                    <select
                                        name="tipoConstruccion"
                                        value={form.tipoConstruccion}
                                        onChange={handleInputChange}
                                        required
                                    >
                                        <option value="">Selecciona</option>
                                        {TIPO_CONSTRUCCION_OPTIONS.map((option) => (
                                            <option key={option} value={option}>{option}</option>
                                        ))}
                                    </select>
                                </label>
                                <label className="mudanza-field">
                                    <span>Metros cuadrados *</span>
                                    <input
                                        type="number"
                                        min="1"
                                        name="metrosCuadrados"
                                        value={form.metrosCuadrados}
                                        onChange={handleInputChange}
                                        placeholder="150"
                                        required
                                    />
                                </label>
                                <label className="mudanza-field">
                                    <span>Número de niveles *</span>
                                    <input
                                        type="number"
                                        min="1"
                                        name="niveles"
                                        value={form.niveles}
                                        onChange={handleInputChange}
                                        placeholder="2"
                                        required
                                    />
                                </label>
                                <label className="mudanza-field">
                                    <span>Fecha de inicio *</span>
                                    <input
                                        type="date"
                                        name="fechaInicioObra"
                                        value={form.fechaInicioObra}
                                        onChange={handleInputChange}
                                        required
                                    />
                                </label>
                                <label className="mudanza-field">
                                    <span>Fecha de término *</span>
                                    <input
                                        type="date"
                                        name="fechaTerminoObra"
                                        value={form.fechaTerminoObra}
                                        onChange={handleInputChange}
                                        required
                                    />
                                </label>
                                <label className="mudanza-field">
                                    <span>Horario de llegada del contratista *</span>
                                    <select
                                        name="horarioLlegada"
                                        value={form.horarioLlegada}
                                        onChange={handleInputChange}
                                        required
                                    >
                                        <option value="">Selecciona un horario</option>
                                        {horarioSlots.map((slot) => (
                                            <option key={slot} value={slot}>
                                                {formatTimeLabel(slot)}
                                            </option>
                                        ))}
                                    </select>
                                </label>
                                <label className="mudanza-field" style={{gridColumn: '1 / -1'}}>
                                    <span>Descripción de la obra *</span>
                                    <textarea
                                        name="descripcionConstruccion"
                                        value={form.descripcionConstruccion}
                                        onChange={handleInputChange}
                                        placeholder="Describe los trabajos..."
                                        rows={3}
                                        required
                                    />
                                </label>
                            </div>
                            <div className="mudanza-info">
                                {allowedDays.length
                                    ? `Respeta los días y horarios permitidos por la administración: ${scheduleSummaryText}`
                                    : 'La administración aún no ha definido días disponibles para permisos de construcción. Contacta para más información.'}
                            </div>
                        </div>
                    )}

                    {step === 2 && (
                        <div className="mudanza-section">
                            <h3>Documentos Requeridos</h3>
                            <p className="mudanza-hint">Los formatos permitidos son PDF, JPG o PNG con un tamaño máximo de 10 MB por archivo.</p>
                            <div className="mudanza-upload-grid">
                                <div className="mudanza-upload-card required">
                                    <header>
                                        <h4>Licencia de construcción municipal</h4>
                                        {files.licencia && (
                                            <button type="button" className="mudanza-upload-remove" onClick={() => handleRemoveFile('licencia')}>
                                                Quitar
                                            </button>
                                        )}
                                    </header>
                                    <label className="mudanza-upload-drop">
                                        <input type="file" accept={ACCEPTED_TYPES.join(',')} onChange={(event) => handleFileSelect(event, 'licencia')} />
                                        <FaCloudUploadAlt />
                                        <strong>{files.licencia ? files.licencia.name : 'Clic para subir archivos'}</strong>
                                        <small>PDF, JPG, PNG (máx. 10MB)</small>
                                    </label>
                                </div>

                                <div className="mudanza-upload-card required">
                                    <header>
                                        <h4>Planos arquitectónicos</h4>
                                        {files.planos && (
                                            <button type="button" className="mudanza-upload-remove" onClick={() => handleRemoveFile('planos')}>
                                                Quitar
                                            </button>
                                        )}
                                    </header>
                                    <label className="mudanza-upload-drop">
                                        <input type="file" accept={ACCEPTED_TYPES.join(',')} onChange={(event) => handleFileSelect(event, 'planos')} />
                                        <FaCloudUploadAlt />
                                        <strong>{files.planos ? files.planos.name : 'Clic para subir archivos'}</strong>
                                        <small>PDF, JPG, PNG (máx. 10MB)</small>
                                    </label>
                                </div>

                                <div className="mudanza-upload-card required">
                                    <header>
                                        <h4>Identificación del propietario</h4>
                                        {files.identificacion && (
                                            <button type="button" className="mudanza-upload-remove" onClick={() => handleRemoveFile('identificacion')}>
                                                Quitar
                                            </button>
                                        )}
                                    </header>
                                    <label className="mudanza-upload-drop">
                                        <input type="file" accept={ACCEPTED_TYPES.join(',')} onChange={(event) => handleFileSelect(event, 'identificacion')} />
                                        <FaCloudUploadAlt />
                                        <strong>{files.identificacion ? files.identificacion.name : 'Clic para subir archivos'}</strong>
                                        <small>PDF, JPG, PNG (máx. 10MB)</small>
                                    </label>
                                </div>

                                <div className="mudanza-upload-card required">
                                    <header>
                                        <h4>Recibo de predial</h4>
                                        {files.predial && (
                                            <button type="button" className="mudanza-upload-remove" onClick={() => handleRemoveFile('predial')}>
                                                Quitar
                                            </button>
                                        )}
                                    </header>
                                    <label className="mudanza-upload-drop">
                                        <input type="file" accept={ACCEPTED_TYPES.join(',')} onChange={(event) => handleFileSelect(event, 'predial')} />
                                        <FaCloudUploadAlt />
                                        <strong>{files.predial ? files.predial.name : 'Clic para subir archivos'}</strong>
                                        <small>PDF, JPG, PNG (máx. 10MB)</small>
                                    </label>
                                </div>

                                <div className="mudanza-upload-card optional">
                                    <header>
                                        <h4>Cronograma (opcional)</h4>
                                        {files.cronograma && (
                                            <button type="button" className="mudanza-upload-remove" onClick={() => handleRemoveFile('cronograma')}>
                                                Quitar
                                            </button>
                                        )}
                                    </header>
                                    <label className="mudanza-upload-drop">
                                        <input type="file" accept={ACCEPTED_TYPES.join(',')} onChange={(event) => handleFileSelect(event, 'cronograma')} />
                                        <FaCloudUploadAlt />
                                        <strong>{files.cronograma ? files.cronograma.name : 'Clic para subir archivos'}</strong>
                                        <small>PDF, JPG, PNG (máx. 10MB)</small>
                                    </label>
                                </div>
                            </div>
                        </div>
                    )}

                    {step === 3 && (
                        <div className="mudanza-section">
                            <h3>Confirmar Solicitud</h3>
                            <div className="mudanza-summary">
                                <div className="mudanza-summary-block">
                                    <h4>Datos del propietario</h4>
                                    <ul>
                                        <li><strong>Nombre:</strong> {form.nombreCompleto || '—'}</li>
                                        <li><strong>Lote:</strong> {form.lote || '—'}</li>
                                        <li><strong>Correo:</strong> {form.email || '—'}</li>
                                        <li><strong>Teléfono:</strong> {form.telefonoPrincipal || '—'}</li>
                                    </ul>
                                </div>
                                <div className="mudanza-summary-block">
                                    <h4>Detalles de la obra</h4>
                                    <ul>
                                        <li><strong>Tipo:</strong> {form.tipoConstruccion || '—'}</li>
                                        <li><strong>Descripción:</strong> {form.descripcionConstruccion || '—'}</li>
                                        <li><strong>Metros cuadrados:</strong> {form.metrosCuadrados ? `${form.metrosCuadrados} m²` : '—'}</li>
                                        <li><strong>Niveles:</strong> {form.niveles || '—'}</li>
                                        <li><strong>Periodo:</strong> {form.fechaInicioObra && form.fechaTerminoObra
                                            ? `${formatDateForSummary(form.fechaInicioObra)} - ${formatDateForSummary(form.fechaTerminoObra)}`
                                            : '—'}</li>
                                        <li><strong>Horario de llegada:</strong> {form.horarioLlegada ? formatTimeLabel(form.horarioLlegada) : '—'}</li>
                                    </ul>
                                </div>
                                <div className="mudanza-summary-block">
                                    <h4>Empresa constructora</h4>
                                    <ul>
                                        <li><strong>Empresa:</strong> {form.empresaConstructora || '—'}</li>
                                        <li><strong>Encargado:</strong> {form.encargadoObra || '—'}</li>
                                        <li><strong>Teléfono:</strong> {form.telefonoEncargado || '—'}</li>
                                        {form.rfcConstructora && <li><strong>RFC:</strong> {form.rfcConstructora}</li>}
                                    </ul>
                                </div>
                                <div className="mudanza-summary-block">
                                    <h4>Documentos</h4>
                                    <ul>
                                        <li><FaCheckCircle /> Licencia municipal</li>
                                        <li><FaCheckCircle /> Planos arquitectónicos</li>
                                        <li><FaCheckCircle /> Identificación del propietario</li>
                                        <li><FaCheckCircle /> Recibo de predial</li>
                                        <li><FaCheckCircle color={files.cronograma ? '#2563eb' : '#94a3b8'} /> Cronograma {files.cronograma ? '(adjunto)' : '(opcional)'}</li>
                                    </ul>
                                </div>
                            </div>

                            <label className="mudanza-terms">
                                <input
                                    type="checkbox"
                                    name="aceptaReglamento"
                                    checked={form.aceptaReglamento}
                                    onChange={handleInputChange}
                                />
                                <span>Acepto el reglamento de construcción y el depósito de garantía.</span>
                            </label>
                        </div>
                    )}
                </div>

                <footer className="mudanza-footer">
                    <button type="button" className="mudanza-btn ghost" onClick={onClose} disabled={saving}>
                        Cancelar
                    </button>
                    <div className="mudanza-footer-actions">
                        {step > 0 && (
                            <button type="button" className="mudanza-btn secondary" onClick={handlePrev} disabled={saving}>
                                <FaArrowLeft /> Anterior
                            </button>
                        )}
                        {step < STEP_TITLES.length - 1 ? (
                            <button type="button" className="mudanza-btn primary" onClick={handleNext} disabled={saving}>
                                Siguiente <FaArrowRight />
                            </button>
                        ) : (
                            <button type="button" className="mudanza-btn primary" onClick={handleSubmit} disabled={saving}>
                                {saving ? 'Enviando…' : (
                                    <>
                                        Enviar <FaCheckCircle />
                                    </>
                                )}
                            </button>
                        )}
                    </div>
                </footer>
            </div>
        </div>
    );
};

SolicitudConstruccionModal.propTypes = {
    fraccionamientoId: PropTypes.string.isRequired,
    currentUser: PropTypes.shape({
        uid: PropTypes.string,
    }).isRequired,
    userData: PropTypes.shape({
        nombreCompleto: PropTypes.string,
        nombre: PropTypes.string,
        apellidos: PropTypes.string,
        displayName: PropTypes.string,
        numeroCasa: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        numeroLote: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        lote: PropTypes.string,
        houseLabel: PropTypes.string,
        telefono: PropTypes.string,
        telefonoPrincipal: PropTypes.string,
        phoneNumber: PropTypes.string,
        correo: PropTypes.string,
        email: PropTypes.string,
        emailPrincipal: PropTypes.string,
        rol: PropTypes.string,
    }),
    onClose: PropTypes.func.isRequired,
    onSuccess: PropTypes.func,
};

export default SolicitudConstruccionModal;
